import { useMemo, useState, type ReactNode } from 'react';
import { color, featuredPanel, font, fontSize, layout, radius } from '../../theme/tokens.js';
import { Disclosure } from '../Disclosure/Disclosure.js';

/**
 * The shared table primitive (FR-032, FR-052, FR-062, Constitution gate 2, R-14).
 *
 * **`fullLabel` is a required property on every column.** That is the mechanism, not a
 * convention: a column literally cannot be declared without its full label, so gate 2's
 * "no column is exempt" is enforced by the type checker rather than by review. Every header
 * then routes through `<Disclosure>`, which gives hover, keyboard focus, and an accessible
 * name the same content (FR-062).
 *
 * Layout mirrors the blueprint: a CSS grid rather than a `<table>`, with `role="table"` and
 * friends so it is still announced as a table. The blueprint's dense 17-column region grid
 * cannot be expressed with `<table>` sizing without fighting it.
 */

export interface Column<Row> {
  key: string;
  /** The short code — the VISIBLE header text (FR-052). */
  label: string;
  /** The full meaning. Required: gate 2 exempts no column. */
  fullLabel: string;
  width?: string;
  align?: 'left' | 'right' | 'center';
  sortable?: boolean;
  render: (row: Row) => ReactNode;
  /** Sort key. Returning `null` sorts the row to the end regardless of direction. */
  sortValue?: (row: Row) => number | string | null;
  /** The totals-row cell. Omitted columns render blank rather than a misleading zero. */
  renderTotal?: () => ReactNode;
}

export interface DataTableProps<Row> {
  columns: Array<Column<Row>>;
  rows: Row[];
  rowKey: (row: Row) => string;
  onRowClick?: (row: Row) => void;
  isRowSelected?: (row: Row) => boolean;
  /**
   * An opt-in background for a row that is not selected — the Audit page's flagged-row tint
   * (spec 007, blueprint `rowBg`). Absent on every other table, which render exactly as before.
   */
  rowTint?: (row: Row) => string | undefined;
  /** Rendered as a totals row beneath the body (FR-032). */
  showTotals?: boolean;
  totalsLabel?: string;
  minWidth?: string;
  caption?: string;
  emptyMessage?: string;
  /**
   * A bar rendered **inside** the card, above the table and separated by a hairline.
   *
   * The blueprint's breakdown is one card — title on the left, filters on the right, table
   * below — not a floating header above a separate card. Passing it in keeps the rounded corner
   * and the border shared, which is the part that reads as wrong when the header sits outside.
   */
  header?: ReactNode;
  /**
   * The sort the table opens with, and the one a third click returns to.
   *
   * Needed because a Delivery table is useful only when it is already ranked — PRD-Delivery §6.6
   * opens on Active TOA descending, so the largest and thinnest books are obvious without
   * clicking anything. Omitted, the table opens in the order the server sent, as Regions does.
   */
  initialSort?: { key: string; direction: 'asc' | 'desc' };
  /**
   * The tallest the scrolling body may get before the table keeps its own scrollbar and pins its
   * header (2026-08-29). `false` opts out — for a table short enough that a cap could only ever
   * add a scrollbar nobody needs.
   *
   * A cap rather than a fixed height, which is what makes this self-adjusting: content shorter
   * than the cap produces no scrollbar and behaves exactly as it did before, so page 1 of a
   * paginated table is untouched while the 134-recruiter roster gains a pinned header.
   */
  maxBodyHeight?: string | false;
  /**
   * The featured card edge (owner request 2026-08-30). Opt-IN, and currently one caller:
   * `<RegionTable>`, whose card is one of the two subjects of the Regions Overview page.
   *
   * A default of `true` would put a brand ring around every table in the product — the Delivery
   * pair, the Specialties table, the by-client breakdown, the roster — at which point the
   * treatment marks nothing.
   */
  featured?: boolean;
  /**
   * Hairlines **between columns** as well as between rows (owner request 2026-09-09). Opt-in, and
   * currently one caller: `<ChartTable>`, whose columns are time buckets.
   *
   * Off everywhere else deliberately. In a table whose rows are the subject — a region, a
   * recruiter, a client — the row is what a reader follows, and a rule at every column boundary
   * competes with it: the blueprint's own tables separate rows and let alignment separate columns.
   * A **matrix** is the case that inverts: with metrics down the side and periods across the top,
   * a cell is read as the intersection of the two, and the reader is crossing the grid in both
   * directions. Turning this on for the twelve dense tables would be exactly the ruled-paper look
   * Principle I keeps them out of.
   *
   * ⚠ It also makes every cell wrapper **stretch** to the row's height (`alignSelf: stretch`),
   * because a rule that stops at the tallest cell's text is worse than no rule. Content inside is
   * centred, so a caller whose cells were already one line high sees no change — but a caller
   * whose cells vary in height will see them centre rather than sit at the row's baseline.
   */
  columnRules?: boolean;
  /**
   * Pin the first column while the rest scrolls sideways (owner request 2026-09-09). Opt-in, one
   * caller, and the same reasoning as `maxBodyHeight`'s pinned header one axis over: a matrix
   * twelve buckets wide does not fit a laptop, and a horizontal scroll that takes the row labels
   * off screen leaves a reader looking at four rows of unattributed figures.
   *
   * The cells it pins carry their own **opaque** background, because the rest of the row now
   * travels underneath them. That is why it is off by default rather than free: a row whose
   * background is set by anything other than selection would need this to know about it.
   */
  stickyFirstColumn?: boolean;
  /**
   * Honour the declared interior gutters — 10px each side of a header cell, 6px of a body cell
   * (2026-09-27). Opt-in, one caller: `<ResultTable>`.
   *
   * ⚠ Every other table gets **0px** there, not the `padding` shorthand's value: `edgePad` hands
   * `undefined` to each longhand that is not on an edge, and React clears a property it is given
   * `undefined` rather than leaving the shorthand's value in place. The fixed tables' width budgets
   * are measured against that 0px (`TWO_FIGURE_TRACK`, Region Performance's 1,270px, the matrix),
   * so correcting it for them moves all four at once.
   *
   * An ad-hoc result has no budget to move, and it is the one table here whose adjacent columns
   * routinely pair a right-aligned figure with a left-aligned label — with no gutter the two
   * headers touch and read as one word (`Open` + `Offering` → `OpenOffering`).
   */
  interiorGutters?: boolean;
}

type SortState = { key: string; direction: 'asc' | 'desc' } | null;

/**
 * The wider gutter the first and last columns get, so their content lines up with the card's own
 * padding instead of running into its border.
 *
 * ⚠ **A CENTRED edge column is exempt, and the exemption is the whole point of this being a
 * function** (2026-09-14). The wide edge is there for content that sits *against* that edge — a
 * left-aligned first column, a right-aligned last one. A centred column runs into neither border,
 * so the override does not protect it from anything; it just makes the cell's padding asymmetric
 * and pushes the content off the track's centre line. In a 68px action column that is 6px on the
 * left against 16px on the right, which put the button **5px left of centre** — visible, because
 * `columnRules` draws the track's two edges and the eye measures against them.
 *
 * It applies to all five action columns at once (Region Performance, By Client, both Delivery
 * tables and the chart matrix), which is why it is fixed here rather than compensated for in each
 * of them with a negative margin that would have to know this function's numbers.
 *
 * ⚠ It changes nothing else: every other centred column in the product is an interior metric
 * column, where this override never applied.
 */
function edgePad<Row>(
  column: Column<Row>,
  index: number,
  count: number,
  edge: number,
  // `undefined` unless the caller opted into `interiorGutters` — see that prop for why.
  interior?: number,
): { paddingLeft?: number; paddingRight?: number } {
  if (column.align === 'center') return {};
  return {
    paddingLeft: index === 0 ? edge : interior,
    paddingRight: index === count - 1 ? edge : interior,
  };
}

export function DataTable<Row>({
  columns,
  rows,
  rowKey,
  onRowClick,
  isRowSelected,
  rowTint,
  showTotals = false,
  totalsLabel = 'TOTAL',
  minWidth = layout.contentMinWidth,
  caption,
  emptyMessage = 'No data for this selection.',
  header,
  initialSort,
  maxBodyHeight = 'min(68vh, 720px)',
  featured = false,
  columnRules = false,
  stickyFirstColumn = false,
  interiorGutters = false,
}: DataTableProps<Row>) {
  const [sort, setSort] = useState<SortState>(initialSort ?? null);
  const headerGutter = interiorGutters ? 10 : undefined;
  const cellGutter = interiorGutters ? 6 : undefined;

  const gridTemplate = columns.map((c) => c.width ?? 'minmax(50px, 1fr)').join(' ');

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const column = columns.find((c) => c.key === sort.key);
    if (!column?.sortValue) return rows;

    const factor = sort.direction === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = column.sortValue!(a);
      const bv = column.sortValue!(b);

      // A value that could not be computed sorts last in BOTH directions. Treating it as 0
      // would rank "we do not know" against real numbers (FR-055).
      if (av === null && bv === null) return 0;
      if (av === null) return 1;
      if (bv === null) return -1;

      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * factor;
      return String(av).localeCompare(String(bv)) * factor;
    });
  }, [rows, sort, columns]);

  /**
   * The two opt-in pieces of cell chrome, in one place so the header, the body and the totals row
   * cannot end up with a rule in two of the three.
   *
   * `background` is the caller's, not a default: the pinned cell has the rest of its row passing
   * underneath it, so it must be opaque in whichever surface that row is painted in — `surface`
   * for a body row, `surfaceSubtle` for the header and the totals row, `surfaceSelected` for a
   * selected one.
   */
  function cellChrome(index: number, background: string) {
    // The pinned column draws the boundary to its right itself, so column 1 does not draw it
    // again — two hairlines a pixel apart read as a 2px rule at one seam and 1px everywhere else.
    const ruled = columnRules && index > 0 && !(stickyFirstColumn && index === 1);

    return {
      ...(ruled ? { borderLeft: `1px solid ${color.borderSubtle}` } : null),
      // A rule that stops at the text is worse than none, so the wrapper takes the row's height
      // and centres its content rather than sitting on the row's centre line itself.
      ...(columnRules || (stickyFirstColumn && index === 0)
        ? { alignSelf: 'stretch', display: 'flex', alignItems: 'center' }
        : null),
      ...(stickyFirstColumn && index === 0
        ? {
            position: 'sticky' as const,
            left: 0,
            // Above its own row's cells, below the sticky header row (2) and its pinned corner (3).
            zIndex: 1,
            background,
            // The seam — the frozen column's edge while the grid travels under it, and the
            // boundary rule for that column pair (see `ruled` above).
            borderRight: `1px solid ${color.borderSubtle}`,
          }
        : null),
    };
  }

  function toggleSort(key: string) {
    setSort((current) => {
      if (current?.key !== key) return { key, direction: 'desc' };
      if (current.direction === 'desc') return { key, direction: 'asc' };
      // Third click clears. With an `initialSort` that means "back to the default ranking"
      // rather than "back to whatever order the server happened to send".
      return initialSort ?? null;
    });
  }

  return (
    <div
      style={{
        background: color.surface,
        border: `1px solid ${color.border}`,
        borderRadius: radius['5xl'],
        overflow: 'hidden',
        // Spread AFTER the defaults so it overrides the hairline, and radius stays this table's.
        ...(featured ? featuredPanel : null),
      }}
    >
      {header ? (
        <div
          style={{
            padding: '14px 20px',
            borderBottom: `1px solid ${color.borderSubtle}`,
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            flexWrap: 'wrap',
          }}
        >
          {header}
        </div>
      ) : null}

      {/*
        The scrollport, in BOTH axes (2026-08-29).

        Wide tables have always scrolled inside their own container so the page never scrolls
        sideways. It now caps its height too, and that is what makes the header stickable: a
        sticky element is positioned against its nearest scroll container, and `overflow-x: auto`
        already made this div that container — with the height unbounded it could never scroll
        vertically, so `top: 0` on the header row had nothing to stick to and did nothing. The
        column names left the screen after eight rows and the reader was looking at fifteen
        columns of unlabelled mono figures.

        `overflow: auto` rather than a pair of axis values on purpose: `overflow-x: auto` with
        `overflow-y: visible` is not a thing the spec allows — `visible` computes to `auto` beside
        a non-visible sibling — so writing it out would only look like it meant something.
      */}
      <div
        style={{
          overflow: 'auto',
          maxHeight: maxBodyHeight === false ? undefined : maxBodyHeight,
        }}
        role="region"
        aria-label={caption ?? 'Data table'}
      >
        <div style={{ minWidth }} role="table" aria-label={caption}>
          <div
            role="row"
            style={{
              display: 'grid',
              gridTemplateColumns: gridTemplate,
              // Opaque, and it has to stay opaque: the rows scroll UNDER this now.
              background: color.surfaceSubtle,
              borderBottom: `1px solid ${color.borderSubtle}`,
              position: 'sticky',
              top: 0,
              // Above the rows, below the ⌘K palette and the disclosure bubbles — those use the
              // popover top layer and so are outside this stacking context entirely.
              zIndex: 2,
            }}
          >
            {columns.map((column, index) => {
              const isSorted = sort?.key === column.key;
              return (
                <div
                  key={column.key}
                  role="columnheader"
                  aria-sort={
                    isSorted ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'
                  }
                  className="sorth"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent:
                      column.align === 'left'
                        ? 'flex-start'
                        : column.align === 'center'
                          ? 'center'
                          : 'flex-end',
                    // 10px interior, 20px at the two outer edges so the first and last columns
                    // line up with the card's own gutter instead of running into its border.
                    padding: '8px 10px',
                    ...edgePad(column, index, columns.length, 20, headerGutter),
                    fontSize: fontSize.tableHeader,
                    fontWeight: 700,
                    letterSpacing: '.2px',
                    color: isSorted ? color.ink : color.inkSubtle,
                    // Which column the ranking is by, without having to find the arrow. Every
                    // blueprint table washes the sorted column this shade, so it belongs here
                    // rather than in any one table.
                    background: isSorted ? color.surfaceSorted : 'transparent',
                    whiteSpace: 'nowrap',
                    userSelect: 'none',
                    // The pinned corner sits above the sticky header row itself, or the first
                    // column's name scrolls under its own header.
                    ...cellChrome(index, isSorted ? color.surfaceSorted : color.surfaceSubtle),
                    ...(stickyFirstColumn && index === 0 ? { zIndex: 3 } : null),
                  }}
                >
                  {/* The short code stays visible; the full label is the disclosure. */}
                  <Disclosure
                    accessibleName={column.fullLabel}
                    onClick={
                      column.sortable !== false && column.sortValue
                        ? () => toggleSort(column.key)
                        : undefined
                    }
                    style={
                      column.sortable !== false && column.sortValue
                        ? { cursor: 'pointer' }
                        : undefined
                    }
                  >
                    <span>{column.label}</span>
                    {isSorted ? (
                      <span className="mono" style={{ fontSize: '8px', color: color.nursing }}>
                        {sort.direction === 'asc' ? '▲' : '▼'}
                      </span>
                    ) : null}
                  </Disclosure>
                </div>
              );
            })}
          </div>

          {sortedRows.length === 0 ? (
            <div
              role="row"
              style={{
                padding: '28px 16px',
                textAlign: 'center',
                color: color.inkSubtle,
                fontSize: fontSize.bodyStrong,
              }}
            >
              {emptyMessage}
            </div>
          ) : (
            sortedRows.map((row) => {
              const selected = isRowSelected?.(row) ?? false;
              return (
                <div
                  key={rowKey(row)}
                  role="row"
                  className="trow"
                  tabIndex={onRowClick ? 0 : undefined}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  onKeyDown={
                    onRowClick
                      ? (event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            onRowClick(row);
                          }
                        }
                      : undefined
                  }
                  style={{
                    display: 'grid',
                    gridTemplateColumns: gridTemplate,
                    alignItems: 'center',
                    borderTop: `1px solid ${color.borderSubtle}`,
                    cursor: onRowClick ? 'pointer' : 'default',
                    background: selected ? color.surfaceSelected : (rowTint?.(row) ?? 'transparent'),
                  }}
                >
                  {columns.map((column, index) => (
                    <div
                      key={column.key}
                      role="cell"
                      style={{
                        minWidth: 0,
                        // The gutter lives on the wrapper, not in each renderer: `<MetricCell>`
                        // is shared with Regions and Delivery, so widening it there would move
                        // three tables to fix one. Its own 4px rides inside this 6px.
                        padding: '0 6px',
                        ...edgePad(column, index, columns.length, 16, cellGutter),
                        ...cellChrome(
                          index,
                          selected ? color.surfaceSelected : color.surface,
                        ),
                      }}
                    >
                      {column.render(row)}
                    </div>
                  ))}
                </div>
              );
            })
          )}

          {showTotals && sortedRows.length > 0 ? (
            <div
              role="row"
              style={{
                display: 'grid',
                gridTemplateColumns: gridTemplate,
                alignItems: 'center',
                borderTop: `2px solid ${color.borderStrong}`,
                background: color.surfaceSubtle,
              }}
            >
              {columns.map((column, index) => (
                <div
                  key={column.key}
                  role="cell"
                  style={{
                    minWidth: 0,
                    // Same gutter as the body, or the totals row sits a few pixels off from the
                    // column it is totalling.
                    padding: '0 6px',
                    ...edgePad(column, index, columns.length, 16, cellGutter),
                    ...cellChrome(index, color.surfaceSubtle),
                  }}
                >
                  {index === 0 ? (
                    <div
                      style={{
                        padding: '8px 4px',
                        fontFamily: font.mono,
                        fontSize: '9.5px',
                        fontWeight: 700,
                        letterSpacing: '.2px',
                        color: color.ink,
                      }}
                    >
                      {totalsLabel}
                    </div>
                  ) : (
                    // A column with no total renders blank. A zero would assert a sum that was
                    // never computed — and the totals row is deliberately heterogeneous
                    // anyway, since the period governs only the activity columns (§9.8.2).
                    (column.renderTotal?.() ?? null)
                  )}
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
